import { chosenSettings } from "./reader-choice.js";
import type { SuggestionWriting } from "../site-search/search-paths.js";

/**
 * Finding the search boxes on a page.
 *
 * `partials/search-form.html` draws a box and wraps it in a `<div>` for the
 * list to hang off. This is the other end of that markup. It is a module of its
 * own so that `search-suggestions.ts` is the behaviour and nothing else, which
 * is the split `nav-menus.ts` and `reader-choice.ts` already make.
 *
 * **A page can carry more than one box.** The rail draws one on every 词典 and
 * 例句 page, and the archives, the entries, the sentences and the two search
 * pages each draw one in the article as well. Both are the same control, so
 * both get the same list, and a reader who reaches for whichever is nearer is
 * answered by it.
 *
 * **The wrapper names nothing, and it used to name two addresses.** The
 * suggestions were published files under hashed paths, so the theme wrote both
 * of them onto the wrapper and the script picked the one the reader was being
 * shown. The route answers now, at an address a browser bundle can build for
 * itself, so a wrapper is a wrapper. What that also settles is the box
 * `src/site-search/search-markup.ts` draws at the edge, which could name no
 * hashed address and had to borrow the rail's. See `docs/search.md`.
 */

/** The wrapper the theme draws around a box, which the list is drawn inside. */
const wrapperSelector = ".cb-search-suggest";

/** The writing setting a 简体 reader is being shown. */
const simplifiedWriting = "zh-CN";

/** What a page has to have before there is anything to wire. */
export interface SearchBox {
  /** The wrapper, which the list is drawn inside. */
  readonly around: HTMLElement;

  /** The form, which a chosen suggestion submits. */
  readonly form: HTMLFormElement;

  /** The box a reader types in. */
  readonly input: HTMLInputElement;
}

/** Every box on this page. */
export function searchBoxes(page: Document): readonly SearchBox[] {
  return [...page.querySelectorAll<HTMLElement>(wrapperSelector)].flatMap(
    (around) => {
      const form = around.querySelector<HTMLFormElement>(".cb-search-form");
      const input = around.querySelector<HTMLInputElement>(".cb-search-input");

      return form === null || input === null ? [] : [{ around, form, input }];
    },
  );
}

/**
 * Which of the two lists the page around the box is drawn in.
 *
 * The same `data-cb-writing` `components/reader-settings.scss` draws the page
 * from, read through `chosenSettings` so that the two cannot disagree about
 * what a reader chose. Everything that is not 简体 is 繁體, Hong Kong's
 * orthography included.
 *
 * Read on every keystroke rather than once, because a reader who changes their
 * writing while the page is open should be offered the other list without
 * reloading.
 */
export function suggestionsFor(box: SearchBox): SuggestionWriting {
  const { writing } = chosenSettings(box.input.ownerDocument);

  return writing === simplifiedWriting ? "hans" : "hant";
}
